/**
 * Zod Schemas for ETL Integration Response Validation
 *
 * These schemas are used to validate responses from the etl_integrations service
 * at runtime, and to derive TypeScript types via z.infer.
 */
import { z } from 'zod';

const EtlStatusDetailsSchema = z.object({
  type: z.string().optional(),
  color: z.string().optional(),
  reason: z.string().optional(),
  display: z.string().optional(),
  statusTimeUTC: z.string().optional(),
  statusTimeOffset: z.string().optional(),
});

const EtlVehicleDetailsSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
});
export type EtlVehicleDetails = z.infer<typeof EtlVehicleDetailsSchema>;

const EtlProviderDetailsSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  phoneNumber: z.string().optional(),
});
export type EtlProviderDetails = z.infer<typeof EtlProviderDetailsSchema>;

const EtlUpdatedBySchema = z.object({
  name: z.string().optional(),
  system: z.string().optional(),
});

const EtlDispatchCenterSchema = z.object({
  name: z.string().optional(),
  phoneNumber: z.string().optional(),
});

const EtlTimeNegotiationSchema = z.object({
  suggestedPickupTimeUTC: z.string().optional(),
  suggestedPickupTimeOffset: z.string().optional(),
});

const EtlUrlSchema = z.object({
  transportTracking: z.string().optional(),
});

const EtlUpdateDetailsSchema = z.object({
  status: EtlStatusDetailsSchema.optional(),
  eta: z.array(z.string()).optional(),
  vehicle: EtlVehicleDetailsSchema.optional(),
  provider: EtlProviderDetailsSchema.optional(),
  updatedBy: EtlUpdatedBySchema.optional(),
  dispatchCenter: EtlDispatchCenterSchema.optional(),
  timeNegotiation: EtlTimeNegotiationSchema.optional(),
});

const EtlStatusVerbatimMessageSchema = z.object({
  update: EtlUpdateDetailsSchema.optional(),
  url: EtlUrlSchema.optional(),
});
export type EtlStatusVerbatimMessage = z.infer<
  typeof EtlStatusVerbatimMessageSchema
>;

const EtlStatusResponseSchema = z.object({
  id: z.number(),
  gmr_trip_id: z.string(),
  created_at: z.string(),
  ingestion_source: z.string(),
  verbatim_message: EtlStatusVerbatimMessageSchema.optional(),
});
export type EtlStatusResponse = z.infer<typeof EtlStatusResponseSchema>;

const EtlTransportResponseSchema = z.object({
  id: z.number(),
  gmr_transport_id: z.string(),
  account_id: z.string(),
  created_at: z.string(),
  is_test: z.boolean(),
  ingestion_source: z.string(),
  verbatim_message: z.record(z.unknown()).optional(),
  statuses: z.array(EtlStatusResponseSchema),
  caller_id: z.string().optional().nullable(),
});
export type EtlTransportResponse = z.infer<typeof EtlTransportResponseSchema>;

const EtlErrorEnvelopeSchema = z.object({
  status: z.literal('failure'),
  detail: z.string(),
});

const EtlSuccessEnvelopeSchema = z.object({
  status: z.literal('success'),
});

/**
 * Create an envelope schema that discriminates on `status`.
 * Success: { status: "success", result: <resultSchema> }
 * Failure: { status: "failure", detail: string }
 */
function etlEnvelope<T extends z.ZodTypeAny>(resultSchema: T) {
  return z.discriminatedUnion('status', [
    z.object({ ...EtlSuccessEnvelopeSchema.shape, result: resultSchema }),
    EtlErrorEnvelopeSchema,
  ]);
}

// POST /api/gmr/transports response
export const EtlCreateTransportResponseSchema = etlEnvelope(
  EtlTransportResponseSchema,
);
export type EtlCreateTransportResponse = z.infer<
  typeof EtlCreateTransportResponseSchema
>;
/** This is @see EtlCreateTransportResponse.result when @see EtlCreateTransportResponse.status == "success" */
export type EtlCreateTransportResult = Extract<
  EtlCreateTransportResponse,
  { status: 'success' }
>['result'];

// GET /api/gmr/transports response
export const EtlListTransportsResponseSchema = etlEnvelope(
  z.object({ transports: z.array(EtlTransportResponseSchema) }),
);
export type EtlListTransportsResponse = z.infer<
  typeof EtlListTransportsResponseSchema
>;
export type EtlListTransportsResult = Extract<
  EtlListTransportsResponse,
  { status: 'success' }
>['result'];

// Transport Request Schema (POST /api/gmr/transports)
export const EtlApiTransportRequestSchema = z.object({
  isTest: z.boolean(),
  isStandby: z.boolean(),
  tripReason: z.string().optional(),
  comments: z.string().optional(),
  callerId: z.string().optional(),
  multipleUnites: z.boolean().optional(),
  latitude: z.number(),
  longitude: z.number(),
  landingZoneId: z.string().optional(),
  pickupLocationComments: z.string().optional(),
  otherResponders: z.boolean(),
  otherRespondersDeclinedWeather: z.boolean(),
  groundContact: z.string().optional(),
});
export type EtlApiTransportRequest = z.infer<
  typeof EtlApiTransportRequestSchema
>;

/**
 * Status display item derived from ETL response for UI rendering
 */
export interface StatusDisplayItem {
  type?: string;
  display?: string;
  timestamp?: string;
  vehicle?: EtlVehicleDetails;
  provider?: EtlProviderDetails;
  trackingUrl?: string;
  eta?: string[];
}
